// MemberDashboard.jsx
// UI restyled to Opera Cloud design system.
// All API calls, data mapping, and business logic are unchanged.

import React, { useState, useEffect } from "react";
import { getMemberSummary, getLocation } from '../../api/operaExpApi';
import StatusBadge from '../../components/StatusBadge';
import '../../styles/operaCloud.css';   // shared design-system tokens
import './MemberDashboard.css';          // page-specific styles

// ─────────────────────────────────────────────────────────────
// Helper — format dates to MM/DD/YYYY
// ─────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  // Treat all null-like values — including the string "null" that the API sometimes returns — as N/A
  if (!dateString
    || dateString === 'N/A'
    || dateString === 'null'
    || dateString === 'undefined'
    || dateString === 'none'
    || dateString === '0000-00-00'
    || dateString === '0000-00-00T00:00:00'
  ) return 'N/A';
  try {
    // Extract only the date part (YYYY-MM-DD) — no timezone conversion
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return dateString;
    return `${month}/${day}/${year}`;
  } catch (e) {
    return dateString;
  }
};

// ─────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────
export default function MemberDashboard() {
  const [loading,             setLoading]             = useState(false);
  const [error,               setError]               = useState(null);
  const [memberSummary,       setMemberSummary]       = useState(null);
  const [searchAccountNumber, setSearchAccountNumber] = useState("");

  // ── API fetch ──────────────────────────────────────────────
  // locationCode is optional — only passed when pmsHotelId was in the URL
  // and getLocation() resolved it successfully.
  const fetchMemberData = async (accountNum, locationCode) => {
    setLoading(true);
    setError(null);

    try {
      console.log('[MemberDashboard] Fetching member data for:', accountNum, locationCode ? `(location: ${locationCode})` : '(no location)');

      const summary = await getMemberSummary(accountNum, locationCode).catch(err => {
        console.warn('[MemberDashboard] getMemberSummary failed:', err);
        return null;
      });

      console.log('[MemberDashboard] Member Summary Response:', summary);

      setMemberSummary(summary);
      // NOTE: loyalty endpoint is not used for the dashboard anymore.
      console.log('[MemberDashboard] All data loaded successfully');
    } catch (err) {
      console.error('Error fetching member data:', err);
      setError(err.message || 'Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  // ── URL param auto-load ────────────────────────────────────
  // Reads memberId (required) and pmsHotelId (optional).
  // When pmsHotelId is present, resolves it to a locationCode via getLocation()
  // and forwards that code to getMemberSummary as ?location-code=...
  useEffect(() => {
    const urlParams     = new URLSearchParams(window.location.search);
    const accountNumber = urlParams.get('memberId') || urlParams.get('accountNumber');
    const pmsHotelId    = urlParams.get('pmsHotelId');

    if (!accountNumber) return;

    console.log('[MemberDashboard] Loading account from URL:', accountNumber);
    setSearchAccountNumber(accountNumber);

    if (pmsHotelId) {
      // Resolve pmsHotelId → locationCode, then fetch member summary with it
      console.log('[MemberDashboard] pmsHotelId found in URL:', pmsHotelId);
      getLocation(pmsHotelId)
        .then(locationRes => {
          const locationCode = locationRes?.locationCode || null;
          console.log('[MemberDashboard] Resolved locationCode:', locationCode);
          fetchMemberData(accountNumber, locationCode);
        })
        .catch(err => {
          // Location lookup failed — still load the member, just without location filter
          console.warn('[MemberDashboard] getLocation failed, proceeding without locationCode:', err);
          fetchMemberData(accountNumber);
        });
    } else {
      // No pmsHotelId — call summary without location code
      fetchMemberData(accountNumber);
    }
  }, []);

  // ── Data mapping (unchanged) ──────────────────────────────
  console.log('[MemberDashboard] Render - memberSummary:', memberSummary ? 'EXISTS' : 'NULL', 'loading:', loading, 'error:', error);

  const accountData = memberSummary?.accountSummary || {};

  const fullName              = accountData.fullName                          || 'N/A';
  const tierDesc              = accountData.tier?.tierDescription             || 'N/A';
  const accountNumber         = accountData.accountNumber                     || 'N/A';
  const dob                   = formatDate(accountData.dateOfBirth);
  // Use tierCredits from the summary endpoint's tier object.
  // Wrap in Number() because the summary endpoint returns these as strings (e.g. "1234"),
  // not numbers. Without the coercion, .toLocaleString() on a string skips comma formatting.
  const tierCredits            = Number(accountData.tier?.tierCredits)              || 0;
  const tierCreditsEarnedToday = Number(accountData.tier?.tierCreditsEarnedToday)   || 0;
  const pointsToday            = Number(accountData.unityPoints?.pointsEarnedToday) || 0;
  const pointsBalance          = Number(accountData.unityPoints?.balance)           || 0;
  const tierExpiration        = formatDate(accountData.tier?.tierEarningsExpiration);
  const lastActivity          = formatDate(accountData.unityPoints?.lastPointActivityDate);
  const memberSince           = formatDate(accountData.unityEnrolledDate);
  const accountType           = accountData.registrationSource                || 'N/A';

  // Status flags (unchanged)
  const statusFlags = {
    discreet:            accountData.discreet            || false,
    excluded:            accountData.excluded            || false,
    merge:               accountData.merge               || false,
    requestedInactive:   accountData.requestedInactive   || false,
    deceased:            accountData.deceased            || false,
    idValidationPending: accountData.idValidationPending || false,
    expired:             accountData.expired             || false,
    // Show CT badge when consentIndicator is explicitly false (consent not given)
    consentIndicator:    accountData.consentIndicator === false,
  };

  const hasStatusFlags = Object.values(statusFlags).some(Boolean);

  // ─────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────
  return (
    <div className="mdPage">
      {/* Hidden header — Opera flex-link provides its own */}
      <div className="mdPageHeader" />

      <div className="mdContainer">

        {/* ── Loading ── */}
        {loading && (
          <div className="sidecarMemberCard">
            <div className="mdEmptyState">
              <div className="mdLoadingIcon">◌</div>
              Loading member dashboard...
            </div>
          </div>
        )}

        {/* ── Error ── */}
        {error && (
          <div className="mdErrorContainer">
            <h3 className="mdErrorTitle">Error Loading Dashboard</h3>
            <div className="mdErrorMessage">{error}</div>
          </div>
        )}

        {/* ── Empty / no data ── */}
        {!memberSummary && !loading && !error && (
          <div className="sidecarMemberCard">
            <div className="mdEmptyState">No data available for this member.</div>
          </div>
        )}

        {/* ── Member card ── */}
        {memberSummary && !loading && (
          <div className="sidecarMemberCard">

            {/* Light Opera-style header: name + account number */}
            <div className="sidecarMemberHeader">
              <div className="sidecarMemberInfo">
                <div className="sidecarMemberName">{fullName}</div>
                <div className="sidecarMemberNumber">{accountNumber}</div>
              </div>
            </div>

            {/* Member Information section */}
            <div className="sidecarSection">
              <div className="sidecarSectionTitle">Member Information</div>

              <div className="sidecarRow">
                <span className="sidecarLabel">Tier</span>
                <span className="sidecarValue">{tierDesc}</span>
              </div>
              <div className="sidecarRow">
                <span className="sidecarLabel">DOB</span>
                <span className="sidecarValue">{dob}</span>
              </div>
              <div className="sidecarRow">
                <span className="sidecarLabel">Member Since</span>
                <span className="sidecarValue">{memberSince}</span>
              </div>
              <div className="sidecarRow">
                <span className="sidecarLabel">Account Type</span>
                <span className="sidecarValue">{accountType}</span>
              </div>
              <div className="sidecarRow">
                <span className="sidecarLabel">Tier Expiration</span>
                <span className="sidecarValue">{tierExpiration}</span>
              </div>
            </div>

            {/* Status badges — only shown when at least one flag is true */}
            {hasStatusFlags && (
              <div className="sidecarBadges">
                {statusFlags.discreet            && <StatusBadge code="DS" tooltip="Ds – Discreet" />}
                {statusFlags.excluded            && <StatusBadge code="EX" tooltip="Ex – Excluded" />}
                {statusFlags.merge               && <StatusBadge code="ME" tooltip="Me – Merge" />}
                {statusFlags.requestedInactive   && <StatusBadge code="RI" tooltip="RI – Requested Inactive" />}
                {statusFlags.deceased            && <StatusBadge code="DE" tooltip="De – Deceased" />}
                {statusFlags.idValidationPending && <StatusBadge code="IV" tooltip="IV – ID Validation Pending" />}
                {statusFlags.expired             && <StatusBadge code="ID" tooltip="ID – Expired" />}
                {statusFlags.consentIndicator    && <StatusBadge code="CT" tooltip="CT – Consent" />}
              </div>
            )}

            {/* Stats — teal accent values */}
            <div className="sidecarStats">
              <div className="sidecarStatRow">
                <span className="sidecarStatLabel">Tier Credits Earned Today</span>
                <span className="sidecarStatValue">{tierCreditsEarnedToday.toLocaleString()}</span>
              </div>
              <div className="sidecarStatRow">
                <span className="sidecarStatLabel">Tier Credits</span>
                <span className="sidecarStatValue">{tierCredits.toLocaleString()}</span>
              </div>
              <div className="sidecarStatRow">
                <span className="sidecarStatLabel">Unity Points Earned Today</span>
                <span className="sidecarStatValue">{pointsToday.toLocaleString()}</span>
              </div>
              <div className="sidecarStatRow">
                <span className="sidecarStatLabel">Unity Points</span>
                <span className="sidecarStatValue">{pointsBalance.toLocaleString()}</span>
              </div>
            </div>

            {/* Last activity */}
            <div className="sidecarLastActivity">
              <span className="sidecarStatLabel">Last Unity Point Activity</span>
              <span className="sidecarStatValue">{lastActivity}</span>
            </div>

          </div>
        )}

      </div>
    </div>
  );
}